// AMO CRM mapping для Flex-заявок — единый источник (правило 1 «Single source
// of truth»): API-синк (apps/api/src/services/flexOrderAmo.service.ts) и
// админ-форма настроек (apps/admin .../CashboxSettings.tsx) импортируют типы и
// дефолты отсюда, а не дублируют значения.
//
// ВАЖНО про дефолты: по умолчанию заданы только id кастом-полей сделки/контакта
// и enum'ов оплаты — они глобальны для AMO-аккаунта и не привязаны к воронке.
// pipelineId и stage-id ДЕФОЛТОВ НЕ ИМЕЮТ: пока админ не впишет их в
// CashboxSettings.amoFlexConfig, синк Flex→AMO выключен (карточка
// flex-amo-default-config, §4.13 — раньше пустая настройка молча гнала
// прод-сделки в тестовую воронку «ТЕСТ N8N»).
import { z } from "zod";
import { FLEX_STATUSES } from "./index.js";
// Кастом-поля сделки — глобальны для AMO-аккаунта (не зависят от воронки),
// поэтому остаются дефолтами и перекрываются настройками поштучно.
export const DEFAULT_FLEX_AMO_FIELDS = Object.freeze({
    from: 1303665,
    to: 1303671,
    senderName: 1328642,
    senderPhone: 1328644,
    senderAddress: 1328646,
    senderPassport: 1328648,
    declaredPrice: 1328650,
    recipientName: 1328652,
    recipientPhone: 1328654,
    messenger: 1328656,
    apartment: 1328658,
    building: 1328660,
    street: 1328662,
    city: 1328664,
    zip: 1328668,
    email: 1328670,
    geo: 1328672,
    pickupFlag: 1339079,
    pickupPhone: 1339081,
    pickupDateTime: 1339083,
    pieces: 1339085,
    packaging: 1339093,
    budget: 1336611,
    weight: 1334897,
    places: 1305337,
    payment: 1308317,
    paidActual: 1339129, // «Оплачено факт» (numeric) — сколько по факту оплатили
    rejectedFlag: 1339117,
    rejectComment: 1339119,
    trackNumber: 0, // не настроено — вписать id поля в CashboxSettings.amoFlexConfig
});
export const DEFAULT_FLEX_AMO_CONTACT_FIELDS = Object.freeze({
    username: 1308573,
    tgId: 1339109,
});
// debt = «ДОЛГ | НЕ ОПЛАЧЕНО» — конвенция «не оплачено/частично» (общая с EMG).
export const DEFAULT_FLEX_AMO_PAYMENT_ENUMS = Object.freeze({
    cash: 845617,
    transfer: 845619,
    card: 845621,
    debt: 845629,
});
/**
 * Полный конфиг-дефолт — единственный референс «дефолтов из кода»: merge-база
 * resolveFlexAmoConfig в API и источник плейсхолдеров def в админ-форме
 * (CashboxSettings.tsx). pipelineId и stages дефолтов НЕ имеют (см. шапку,
 * §4.13): 0 = «не настроено», синк выключен. Вписывать сюда реальные id
 * воронки/этапов запрещено — прод-воронка живёт ТОЛЬКО в БД
 * (CashboxSettings.amoFlexConfig), не в коде (правило 2 «Архитектурных правил»).
 */
export const DEFAULT_FLEX_AMO_CONFIG = Object.freeze({
    pipelineId: 0,
    stages: Object.freeze(Object.fromEntries(FLEX_STATUSES.map((s) => [s, 0]))),
    fields: DEFAULT_FLEX_AMO_FIELDS,
    contactFields: DEFAULT_FLEX_AMO_CONTACT_FIELDS,
    paymentEnums: DEFAULT_FLEX_AMO_PAYMENT_ENUMS,
    // null = граница не задана → MnP патчит сделку на всех статусах (текущее
    // поведение). Прод-значение ("customs") ставит владелец через настройки/SQL,
    // в код не зашивается (правило 2 «Архитектурных правил»).
    handoffAfter: null,
});
// ── PATCH-схема настроек (CashboxSettings.amoFlexConfig) ─────────────────────
// Частичный конфиг поверх дефолтов: шлются только явно заданные id. Все id —
// положительные целые; 0 слать нельзя («не настроено» = отсутствие ключа,
// admin-форма опирается на это — см. buildFlexConfig в CashboxSettings.tsx).
// .strict() — ОСОЗНАННО (вердикт ревью волны 3): это денежный конфиг, опечатка
// ключа обязана падать 400 на границе API, а не тихо персистится в jsonb и
// игнорироваться resolveFlexAmoConfig (класс тихих отказов §4.13). «Сохранение
// неизвестных ключей под волну 4» — ложная гарантия: админ-форма пересобирает
// конфиг из известных ей ключей, а сервер заменяет jsonb целиком. Волна 4
// (handoffAfter) расширяет ЭТУ схему явным типизированным полем в том же PR,
// что и enforcement-код — деплой admin/api атомарный. (Allowlist воронок волны 4
// сюда НЕ входит: он живёт в env AMO_ALLOWED_PIPELINE_IDS, не в БД — БД может
// оказаться прод-дампом на стенде, карточка amo-test-pipeline-guard §7.3a.)
// Ключи групп тоже строгие: stages — только FLEX_STATUSES, остальные группы —
// только ключи соответствующих DEFAULT_*-объектов (листовая опечатка → 400).
const flexAmoId = z.number().int().positive();
function strictIdMap(keys) {
    return z
        .object(Object.fromEntries(keys.map((k) => [k, flexAmoId.optional()])))
        .strict();
}
export const flexAmoConfigPatchSchema = z
    .object({
    pipelineId: flexAmoId.optional(),
    stages: strictIdMap(FLEX_STATUSES).optional(),
    fields: strictIdMap(Object.keys(DEFAULT_FLEX_AMO_FIELDS)).optional(),
    contactFields: strictIdMap(Object.keys(DEFAULT_FLEX_AMO_CONTACT_FIELDS)).optional(),
    paymentEnums: strictIdMap(Object.keys(DEFAULT_FLEX_AMO_PAYMENT_ENUMS)).optional(),
    // Волна 4: граница «MnP ведёт сделку». Значение — статус из FLEX_STATUSES
    // (после него MnP не патчит сделку) или null = граница снята (патчим всё).
    handoffAfter: z
        .enum([...FLEX_STATUSES])
        .nullable()
        .optional(),
})
    .strict();
/**
 * Синк Flex→AMO считается настроенным, только когда воронка И все 12 этапов
 * заданы явно (положительные id). Единственный источник правила полноты:
 * API (resolveFlexAmoConfig.complete) и админ-форма настроек используют эту
 * же функцию. При false синк выключен — push/patch не зовут AMO API.
 */
export function flexAmoSyncConfigured(pipelineId, stages) {
    return pipelineId > 0 && FLEX_STATUSES.every((s) => (stages[s] ?? 0) > 0);
}
// ── Статус синка с AMO (FlexOrder.amoStatus) ─────────────────────────────────
// "not_configured" — воронка/этапы не заданы в настройках, синк пропущен;
// "blocked_env" — воронка настроена, но НЕ входит в env-allowlist
//   AMO_ALLOWED_PIPELINE_IDS этого окружения (карточка amo-test-pipeline-guard,
//   §7.3a: тестовый стенд с прод-дампом БД не должен пушить в прод-воронку) —
//   синк пропущен, создание/обновление заявки не блокируется;
// "ok" — последний push/patch в AMO прошёл успешно. NULL в БД — заявка ещё ни
// разу не пыталась синкаться (или создана до введения поля).
export const FLEX_AMO_SYNC_STATUSES = Object.freeze([
    "ok",
    "not_configured",
    "blocked_env",
]);
export const FLEX_AMO_SYNC_STATUS_LABELS_RU = {
    ok: "Синхронизировано с AMO",
    not_configured: "AMO не настроен",
    blocked_env: "AMO заблокирован окружением",
};
//# sourceMappingURL=amo.js.map